import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Overview from '../components/customer/Overview';
import Tenants from '../components/customer/Tenants';
import Subscriptions from '../components/customer/Subscriptions';
import Invoices from '../components/customer/Invoices';
import { customerServices } from '../lib/supabase';
import type { Customer } from '../lib/supabase';

const CustomerDetail = () => {
  const [activeTab, setActiveTab] = useState('Overview');
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    const fetchCustomer = async () => {
      if (!id) {
        setError('No customer ID provided');
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await customerServices.getById(id);
        if (error) throw error;
        setCustomer(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch customer details');
      } finally {
        setLoading(false);
      }
    };

    fetchCustomer();
  }, [id]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'Overview':
        return <Overview />;
      case 'Tenants':
        return <Tenants />;
      case 'Subscriptions':
        return <Subscriptions />;
      case 'Invoices':
        return <Invoices />;
      default:
        return <Overview />;
    }
  };

  if (loading) return <div className="text-center p-4">Loading...</div>;
  if (error) return <div className="text-red-600 p-4">{error}</div>;
  if (!customer) return <div className="text-center p-4">Customer not found</div>;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">{customer.name}</h1>
        <p className="text-gray-500">{customer.email}</p>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
            {['Overview', 'Tenants', 'Subscriptions', 'Invoices'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab}
              </button>
            ))}
          </nav>
        </div>
        <div className="p-6">{renderTabContent()}</div>
      </div>
    </div>
  );
};

export default CustomerDetail;
